import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { Vitals } from '../types';
import { Target, Activity, Info, TrendingUp, TrendingDown, Scale } from 'lucide-react';

/**
 * Props for the BmiTrendChart component.
 * Supports passing the list of patient vital histories, as well as target BMI 
 * and custom mutation callback for clinican-defined goals.
 */
interface BmiTrendChartProps {
  vitals: Vitals[];
  targetBmi?: number;
  onTargetBmiChange?: (target: number) => Promise<void>;
}

export default function BmiTrendChart({ vitals, targetBmi, onTargetBmiChange }: BmiTrendChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [targetInput, setTargetInput] = useState<string>(targetBmi?.toString() || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState({ width: 500, height: 260 });

  // Sync state if targetBmi updates elsewhere
  useEffect(() => {
    if (targetBmi !== undefined) {
      setTargetInput(targetBmi.toString());
    }
  }, [targetBmi]);

  // Handle responsive resizing utilizing ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width } = entry.contentRect;
        // Keep height responsive and fluid
        setDimensions({
          width: Math.max(width, 300),
          height: 260
        });
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Compute calculated statistics for clinician review
  const sortedData = [...(vitals || [])]
    .filter(v => v.bmi && !isNaN(v.bmi))
    .sort((a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime());

  const currentBmi = sortedData.length > 0 ? sortedData[sortedData.length - 1].bmi : null;
  const initialBmi = sortedData.length > 1 ? sortedData[0].bmi : null;
  const bmiDelta = currentBmi !== null && initialBmi !== null ? parseFloat((currentBmi - initialBmi).toFixed(1)) : null;

  // Classify current BMI range according to strict clinical WHO guidelines
  const getBmiCategory = (bmi: number) => {
    if (bmi < 18.5) return { label: 'Underweight', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' };
    if (bmi < 25.0) return { label: 'Normal Weight', color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' };
    if (bmi < 30.0) return { label: 'Overweight', color: 'text-amber-500', bg: 'bg-amber-50/50 border-amber-100' };
    return { label: 'Obese', color: 'text-rose-600', bg: 'bg-rose-50 border-rose-200' };
  };

  const currentCategory = currentBmi ? getBmiCategory(currentBmi) : null;

  // Draw chart inside the SVG canvas container
  useEffect(() => {
    if (!chartRef.current || sortedData.length === 0) return;

    // Clear any leftover SVG nodes
    d3.select(chartRef.current).selectAll('*').remove();

    const margin = { top: 25, right: 30, bottom: 40, left: 45 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', dimensions.width)
      .attr('height', dimensions.height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Parse absolute timestamps
    const data = sortedData.map(v => ({
      date: new Date(v.recordedAt),
      bmi: v.bmi,
      weight: v.weight,
      height: v.height
    }));

    // Setup Scales
    const x = d3.scaleTime()
      .domain(d3.extent(data, d => d.date) as [Date, Date])
      .range([0, width]);

    // Track scale range, ensuring boundaries capture typical WHO groups
    const maxBmiInData = d3.max(data, d => d.bmi) || 25;
    const minBmiInData = d3.min(data, d => d.bmi) || 20;
    const domainUpper = Math.max(32, maxBmiInData + 2, targetBmi || 0);
    const domainLower = Math.min(16, minBmiInData - 2);

    const y = d3.scaleLinear()
      .domain([domainLower, domainUpper])
      .range([height, 0]);

    // Draw grid lines
    svg.append('g')
      .attr('class', 'grid')
      .attr('opacity', 0.1)
      .call(d3.axisLeft(y).ticks(6).tickSize(-width).tickFormat(() => ''));

    // Draw Color Shaded Clinical Weight Zones (EHR backdrop)
    const zones = [
      { y1: 16, y2: 18.5, color: '#fef3c7', label: 'Underweight' }, // Amber-100
      { y1: 18.5, y2: 25, color: '#ecfdf5', label: 'Normal' },      // Emerald-50
      { y1: 25, y2: 30, color: '#fffbeb', label: 'Overweight' },  // Amber-50
      { y1: 30, y2: domainUpper, color: '#fef2f2', label: 'Obese' } // Rose-50
    ];

    zones.forEach(zone => {
      const yStart = y(Math.min(zone.y2, domainUpper));
      const yEnd = y(Math.max(zone.y1, domainLower));
      
      if (yEnd > yStart) {
        svg.append('rect')
          .attr('x', 0)
          .attr('y', yStart)
          .attr('width', width)
          .attr('height', yEnd - yStart)
          .attr('fill', zone.color)
          .attr('opacity', 0.45);

        // Zone text anchor label
        svg.append('text')
          .attr('x', 6)
          .attr('y', yStart + 12)
          .attr('fill', '#64748b')
          .attr('font-size', '8px')
          .attr('font-weight', 'semibold')
          .attr('opacity', 0.7)
          .text(zone.label);
      }
    });

    // X Axis Setup
    svg.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(5).tickFormat(d3.timeFormat('%Y-%m-%d') as any))
      .attr('font-size', '9px')
      .attr('color', '#94a3b8');

    // Y Axis Setup
    svg.append('g')
      .call(d3.axisLeft(y).ticks(6))
      .attr('font-size', '9px')
      .attr('color', '#94a3b8');

    // Chart target goal dashed line helper
    if (targetBmi && targetBmi > 0) {
      svg.append('line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', y(targetBmi))
        .attr('y2', y(targetBmi))
        .attr('stroke', '#4f46e5')
        .attr('stroke-width', 1.8)
        .attr('stroke-dasharray', '4,4');

      svg.append('text')
        .attr('x', width - 8)
        .attr('y', y(targetBmi) - 5)
        .attr('fill', '#4f46e5')
        .attr('font-size', '9px')
        .attr('font-weight', 'bold')
        .attr('text-anchor', 'end')
        .text(`Target Goals: ${targetBmi} BMI`);
    }

    // Line Generator for BMI Curve
    const bmiLine = d3.line<any>()
      .x(d => x(d.date))
      .y(d => y(d.bmi))
      .curve(d3.curveMonotoneX);

    // Render Gradient Glow Path Backdrop
    const gradient = svg.append("defs")
      .append("linearGradient")
      .attr("id", "bmi-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "0%").attr("y2", "100%");

    gradient.append("stop").attr("offset", "0%").attr("stop-color", "#3b82f6").attr("stop-opacity", 0.3);
    gradient.append("stop").attr("offset", "100%").attr("stop-color", "#3b82f6").attr("stop-opacity", 0.0);

    const areaGen = d3.area<any>()
      .x(d => x(d.date))
      .y0(height)
      .y1(d => y(d.bmi))
      .curve(d3.curveMonotoneX);

    svg.append('path')
      .datum(data)
      .attr('fill', 'url(#bmi-gradient)')
      .attr('d', areaGen);

    // Primary trend path line
    svg.append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 2.5)
      .attr('d', bmiLine);

    // Patient dots on coordinate intersections
    const dots = svg.selectAll('.dot-group')
      .data(data)
      .enter()
      .append('g')
      .attr('class', 'dot-group');

    dots.append('circle')
      .attr('cx', d => x(d.date))
      .attr('cy', d => y(d.bmi))
      .attr('r', 5)
      .attr('fill', '#1d4ed8')
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 1.5)
      .attr('class', 'cursor-pointer hover:r-7 transition-all');

    // Render tiny metric tags over coordinate entries to expedite review
    dots.append('text')
      .attr('x', d => x(d.date))
      .attr('y', d => y(d.bmi) - 9)
      .attr('text-anchor', 'middle')
      .attr('font-size', '8.5px')
      .attr('font-weight', 'extrabold')
      .attr('fill', '#1e293b')
      .text(d => d.bmi.toFixed(1));

  }, [sortedData, dimensions, targetBmi]);

  // Handle goal submissions with validation
  const handleSaveGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setIsSubmitting(true);

    try {
      const numValue = parseFloat(targetInput);
      if (isNaN(numValue) || numValue < 10 || numValue > 60) {
        throw new Error('Please specify a reasonable target BMI between 10.0 and 60.0');
      }

      if (onTargetBmiChange) {
        // Run clinical action
        await onTargetBmiChange(numValue);
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Error occurred while saving patient target settings.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (vitals.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6 text-center shadow-sm">
        <Scale className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <p className="text-slate-500 text-xs font-semibold">No recorded BMI timeline records found.</p>
        <p className="text-[10px] text-slate-400 mt-1">Please record patient height and weight inside clinical logs to automatically trace their progress.</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
      
      {/* Dynamic Dashboard Metrics Header Group */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 border-b border-slate-100 pb-3">
        <div>
          <div className="flex items-center gap-2">
            <Scale className="w-4 h-4 text-blue-600" />
            <span className="font-sans font-bold text-slate-800 text-sm tracking-tight">Visit-by-Visit Clinical BMI Index</span>
          </div>
          <p className="text-[10px] text-slate-500 mt-0.5">Chronological progress from EHR intake registries</p>
        </div>

        {/* BMI Progress indicator cards */}
        {currentBmi !== null && (
          <div className="flex items-center gap-2 text-xs">
            {/* Category badge */}
            {currentCategory && (
              <div className={`px-2.5 py-1 rounded-md text-[10px] font-extrabold border ${currentCategory.bg} ${currentCategory.color}`}>
                Current: {currentBmi.toFixed(1)} — {currentCategory.label}
              </div>
            )}
            
            {/* Delta trend tracking */}
            {bmiDelta !== null && bmiDelta !== 0 && (
              <div className={`flex items-center gap-1 font-bold px-2 py-0.5 rounded text-[10px] ${bmiDelta < 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                {bmiDelta < 0 ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}
                {bmiDelta < 0 ? '' : '+'}{bmiDelta} since intake
              </div>
            )}
          </div>
        )}
      </div>

      {vitals.length < 2 ? (
        <div className="bg-slate-50 text-slate-500 text-[11px] p-6 text-center rounded-lg border border-slate-100 italic">
          Additional vital registers are needed to compile a chronological progression timeline curve (Needs at least 2 logs).
        </div>
      ) : (
        /* D3 Render Target Canvas */
        <div className="relative">
          <div ref={chartRef} className="mx-auto select-none bg-slate-50 rounded-xl p-2 border border-slate-100" />
        </div>
      )}

      {/* Target BMI Input Panel for Clinicians */}
      <div className="bg-slate-50 border border-slate-150 rounded-lg p-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
        <div className="md:col-span-7 flex gap-2 items-start text-xs">
          <Target className="w-4.5 h-4.5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-bold text-slate-700 block text-[11px]">Sync Goal & Management Pathway</span>
            <span className="text-[10px] text-slate-500">Provide an ideal benchmark BMI target. Ideal targets are typically 18.5 – 24.9.</span>
          </div>
        </div>
        
        <form onSubmit={handleSaveGoal} className="md:col-span-5 flex flex-col gap-1.5 w-full">
          <div className="flex gap-2 w-full justify-end">
            <input 
              type="number" 
              step="0.1"
              value={targetInput}
              onChange={(e) => setTargetInput(e.target.value)}
              className="w-16 bg-white border border-slate-200 text-xs font-bold text-slate-800 rounded px-2 py-1 outline-none text-center focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 focus:ring-opacity-50"
              placeholder="e.g. 22.5"
              disabled={isSubmitting}
            />
            <button 
              type="submit"
              disabled={isSubmitting}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-[11px] px-3 py-1 rounded transition disabled:opacity-50 active:scale-95 duration-150"
            >
              {isSubmitting ? 'Syncing...' : 'Set Target'}
            </button>
          </div>
          {errorMsg && (
            <span className="text-[9.5px] text-red-600 font-bold block text-right mt-0.5">{errorMsg}</span>
          )}
        </form>
      </div>

      {/* General Clinical Guidance footer */}
      <div className="flex items-start gap-1.5 text-[10px] text-slate-400 bg-white p-2.5 rounded border border-dashed border-slate-150">
        <Info className="w-3.5 h-3.5 text-slate-300 flex-shrink-0 mt-0.5" />
        <p className="leading-snug">
          BMI is a surrogate marker of fatness, configured as mass (kg) divided by height squared (m²). It is highly useful in tracking outpatient diabetic guidelines, cardiometabolic risks, and surgical candidacy.
        </p>
      </div>

    </div>
  );
}
